import { useState, useCallback, useRef } from "react";
import { useMutation } from "@apollo/client";
import getClientIp from "core/utils/getClientIp";
import {
  CreateUserBookReadMutation,
  RecordUserBookEventMutation,
} from "../mutations";

/**
 * Хук для работы с чтением книг
 * Управляет созданием сессии чтения, записью прогресса
 */
export default function useBookReader() {
  const [currentReadId, setCurrentReadId] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [bookData, setBookData] = useState(null);
  const readingStartTime = useRef(null);
  const lastProgressTime = useRef(null);
  // Ref-копии для надёжного использования в cleanup-эффектах и visibilitychange
  const currentReadIdRef = useRef(null);
  const isReadingRef = useRef(false);

  const [createUserBookRead] = useMutation(CreateUserBookReadMutation);
  const [recordUserBookEvent] = useMutation(RecordUserBookEventMutation);

  /**
   * Начать чтение книги
   * @param {string} token - токен доступа к книге
   * @param {object} utm - UTM параметры
   * @param {string} getIpUrl - URL для получения IP адреса клиента
   */
  const startReading = useCallback(
    async (token, utm = {}, getIpUrl = null) => {
      try {
        // Получаем IP клиента если предоставлен getIpUrl
        let clientIp = null;
        if (getIpUrl) {
          try {
            clientIp = await getClientIp(getIpUrl);
          } catch (error) {
            console.error("Failed to get client IP:", error);
          }
        }

        const { data } = await createUserBookRead({
          variables: {
            input: {
              token,
              utm,
            },
            clientIp,
          },
        });

        if (data?.createUserBookRead) {
          const readData = data.createUserBookRead;
          setCurrentReadId(readData.id);
          currentReadIdRef.current = readData.id;

          // Устанавливаем данные книги с прогрессом
          setBookData({
            ...readData.book,
          });

          setIsReading(true);
          isReadingRef.current = true;
          readingStartTime.current = Date.now();
          lastProgressTime.current = Date.now();

          return readData;
        }
      } catch (error) {
        console.error("Failed to start reading:", error);
        throw error;
      }
    },
    [createUserBookRead]
  );

  /**
   * Записать прогресс чтения
   * @param {number} currentCharacter - текущая позиция в символах
   * @param {number} charactersRead - количество прочитанных символов с последнего обновления
   * @param {number} fraction - точная позиция как дробь от 0.0 до 1.0
   * @param {string} cfi - точная позиция в формате CFI
   */
  const recordProgress = useCallback(
    async (
      currentCharacter,
      charactersRead = 0,
      fraction = null,
      cfi = null
    ) => {
      // Используем ref-копии чтобы работать корректно в cleanup/visibilitychange
      if (!currentReadIdRef.current || !isReadingRef.current) return;

      const now = Date.now();
      const durationMs = lastProgressTime.current
        ? now - lastProgressTime.current
        : 0;
      lastProgressTime.current = now;

      try {
        const { data } = await recordUserBookEvent({
          variables: {
            input: {
              readId: currentReadIdRef.current,
              currentCharacter,
              charactersRead,
              durationMs,
              fraction,
              cfi,
            },
          },
        });

        return data?.recordUserBookEvent;
      } catch (error) {
        console.error("Failed to record progress:", error);
        throw error;
      }
    },
    [recordUserBookEvent]
  );

  /**
   * Завершить чтение книги
   * @param {number} currentCharacter - финальная позиция в символах
   * @param {number} charactersRead - количество прочитанных символов в финальной сессии
   */
  const finishReading = useCallback(
    async (currentCharacter, charactersRead = 0) => {
      if (!currentReadIdRef.current || !isReadingRef.current) return;

      const now = Date.now();
      const durationMs = lastProgressTime.current
        ? now - lastProgressTime.current
        : 0;

      // Сбрасываем состояние через refs немедленно, чтобы не было повторных вызовов
      isReadingRef.current = false;
      const readId = currentReadIdRef.current;
      currentReadIdRef.current = null;
      lastProgressTime.current = null;
      readingStartTime.current = null;

      setIsReading(false);
      setCurrentReadId(null);
      setBookData(null);

      try {
        const { data } = await recordUserBookEvent({
          variables: {
            input: {
              readId,
              currentCharacter,
              charactersRead,
              durationMs,
            },
          },
        });

        return data?.recordUserBookEvent;
      } catch (error) {
        console.error("Failed to finish reading:", error);
        throw error;
      }
    },
    [recordUserBookEvent]
  );

  /**
   * Получить общее время чтения в текущей сессии
   */
  const getTotalReadingTime = useCallback(() => {
    if (!readingStartTime.current) return 0;
    return Date.now() - readingStartTime.current;
  }, []);

  return {
    // Состояние
    currentReadId,
    isReading,
    bookData,

    // Методы
    startReading,
    recordProgress,
    finishReading,
    getTotalReadingTime,
  };
}
